import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { AuthShell, AuthHeading } from '@/components/auth/AuthShell';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthStatus } from '@/components/auth/AuthStatus';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { isNetworkError, NETWORK_ERROR_MESSAGE } from '@/services/network';

function ResetPasswordContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [bootstrapping, setBootstrapping] = useState(true);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const bootstrap = async () => {
      try {
        // Short link from our reset email (coach worker /go/{code}): redeem the
        // token hash here, so inbox scanners that pre-open links can't spend it.
        const tokenHash = searchParams.get('token_hash');
        if (tokenHash && searchParams.get('type') === 'recovery') {
          window.history.replaceState(null, '', window.location.pathname);
          const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
          if (verifyError) {
            setSessionError('This reset link has expired or was already used. Please request a new one.');
            setBootstrapping(false);
            return;
          }
          setSessionReady(true);
          setBootstrapping(false);
          return;
        }

        // Supabase redirects back with #error_code=otp_expired when its link is spent.
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        if (hashParams.get('error_code')) {
          setSessionError(hashParams.get('error_code') === 'otp_expired'
            ? 'This reset link has expired or was already used. Please request a new one.'
            : 'This reset link is invalid. Please request a new one.');
          setBootstrapping(false);
          return;
        }

        // Check for PKCE code param
        const code = searchParams.get('code');
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            setSessionError('This reset link is invalid or has expired. Please request a new one.');
            setBootstrapping(false);
            return;
          }
          setSessionReady(true);
          setBootstrapping(false);
          return;
        }

        // Check for implicit flow access_token in hash fragment
        if (typeof window !== 'undefined') {
          const hash = window.location.hash;
          if (hash) {
            const params = new URLSearchParams(hash.substring(1));
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            if (accessToken) {
              const { error: sessionErr } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || '',
              });
              if (sessionErr) {
                setSessionError('This reset link is invalid or has expired. Please request a new one.');
                setBootstrapping(false);
                return;
              }
              setSessionReady(true);
              setBootstrapping(false);
              return;
            }
          }
        }

        // Check if user already has a valid session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setSessionReady(true);
        } else {
          setSessionError('No valid reset session found. Please request a new password reset link.');
        }
      } catch (err: unknown) {
        setSessionError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Failed to validate reset link. Please try again.');
      } finally {
        setBootstrapping(false);
      }
    };

    bootstrap();
  }, []);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword) { setError('New password is required.'); return; }
    if (newPassword.length < 10) { setError('Password must be at least 10 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      // password_set_at: same stamp the portal's PasswordPanel writes, so the
      // forgot-password gate (auth_email_has_password) sees a password from now on.
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        data: { password_set_at: new Date().toISOString() },
      });
      if (updateError) {
        setError(isNetworkError(updateError) ? NETWORK_ERROR_MESSAGE : updateError.message || 'Failed to update password. Please try again.');
        return;
      }
      // Success: sign out of THIS browser only and go to sign in. A global
      // sign-out would also end the creator's BMC app sessions.
      await supabase.auth.signOut({ scope: 'local' });
      navigate('/auth?reset=success', { replace: true });
    } catch (err: unknown) {
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (bootstrapping) {
    return (
      <AuthShell>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </AuthShell>
    );
  }

  if (!sessionReady && sessionError) {
    return (
      <AuthShell>
        <AuthStatus
          tone="danger"
          icon={<AlertTriangle size={30} />}
          title="Link expired"
          action={
            <Button variant="primary" onClick={() => navigate('/forgot-password', { replace: true })}>
              Request new link
            </Button>
          }
        >
          {sessionError}
        </AuthStatus>
      </AuthShell>
    );
  }

  const remaining = 10 - newPassword.length;

  return (
    <AuthShell>
      <AuthHeading title="Set new password" subtitle="Choose a strong password for your account." />

      <form onSubmit={handleUpdate} className="mt-6 flex flex-col gap-4">
        <PasswordInput
          placeholder="New password (min 10 characters)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
        />

        <PasswordInput
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
        />

        {newPassword.length > 0 && remaining > 0 && (
          <p className="px-1 text-[13px] text-white/40">
            {remaining} more character{remaining !== 1 ? 's' : ''} needed
          </p>
        )}

        {error && <AuthAlert variant="error">{error}</AuthAlert>}

        <Button type="submit" variant="primary" fullWidth loading={loading} className="mt-2">
          Update Password
        </Button>
      </form>
    </AuthShell>
  );
}

export default function ResetPasswordPage() {
  return <ResetPasswordContent />;
}
